import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { playSound } from '../../audio/sounds.js';
import { GameOverContext } from '../common/TurnStatus.js';
import { SnakeBoard, SnakePad, useSnakeControls, useSnakeTheme } from './SnakeBoard.js';
import type { ArenaSocket } from '../../realtime.js';
import type { ArenaResult } from '../../../shared/protocol.js';
import type { SnakeDir, SnakeGameView } from '../../../shared/games/snake/index.js';

/**
 * State of the running snake game: pushed by the server on every step, re-requested after
 * reconnecting. After the game has ended the last state is kept.
 */
export function useSnakeState(socket: ArenaSocket | null, gameId: number, enabled: boolean): { state: SnakeGameView | null; offset: number } {
  const [state, setState] = useState<SnakeGameView | null>(null);
  const offset = useRef(0);
  useEffect(() => {
    if (!socket || !enabled) return;
    const onState = (s: SnakeGameView) => {
      offset.current = s.serverNow - Date.now();
      setState(s);
    };
    const sync = () =>
      socket.emit('snake:sync', (r: ArenaResult<{ state: SnakeGameView | null }>) => {
        if (r.ok && r.state) onState(r.state);
      });
    socket.on('snake:state', onState);
    socket.on('connect', sync);
    if (socket.connected) sync();
    return () => {
      socket.off('snake:state', onState);
      socket.off('connect', sync);
    };
  }, [socket, gameId, enabled]);
  return { state: state && state.id === gameId ? state : null, offset: offset.current };
}

const secondsText = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Two-player snake (decision 2026-09-30): the server runs the game, this view draws and steers. */
export function SnakeGame(props: { state: SnakeGameView; offset: number; socket: ArenaSocket }) {
  const { state, socket } = props;
  const over = useContext(GameOverContext);
  // The theme is chosen in the lobby before the game (user request 2026-09-30).
  const [theme] = useSnakeTheme();
  const area = useRef<HTMLDivElement>(null);
  const [, setNow] = useState(0);
  const serverNow = Date.now() + props.offset;
  const me = state.players[state.mySeat]!;
  const opponent = state.players[1 - state.mySeat]!;
  const playing = !over && state.phase !== 'over' && me.alive;

  const turn = (dir: SnakeDir) => {
    if (playing) socket.emit('snake:turn', { dir });
  };
  useSnakeControls(turn, area, playing);

  // Countdown and the clock tick between the steps.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, []);

  // Sounds: eating, crashes and the countdown beeps.
  const prev = useRef<SnakeGameView | null>(null);
  useEffect(() => {
    const before = prev.current;
    prev.current = state;
    if (!before || before.id !== state.id) return;
    if (me.score > before.players[state.mySeat]!.score) playSound('snakeEat');
    if (state.players.some((p, i) => !p.alive && before.players[i]!.alive)) playSound('snakeCrash');
  }, [state]);
  const countdown = state.phase === 'countdown' ? Math.ceil((state.startsAt - serverNow) / 1000) : 0;
  const lastBeep = useRef(0);
  useEffect(() => {
    if (countdown > 0 && countdown !== lastBeep.current) {
      lastBeep.current = countdown;
      playSound('snakeCount');
    }
  }, [countdown]);

  const overlay =
    state.phase === 'countdown' ? (countdown > 0 ? String(countdown) : t.snake.go) : !me.alive && !over ? t.snake.crashed : null;

  return (
    <div class="snake">
      <div class="snake-hud">
        <div class="snake-score mine">
          <span class="snake-swatch mine" aria-hidden="true" />
          {t.snake.you}: <strong>{me.body.length}</strong>
        </div>
        <div class="snake-clock" aria-label={t.snake.timeLeft}>
          ⏱ {secondsText(state.phase === 'countdown' ? state.endsAt - state.startsAt : state.endsAt - serverNow)}
        </div>
        <div class="snake-score theirs">
          <span class="snake-swatch theirs" aria-hidden="true" />
          {opponent.displayName}: <strong>{opponent.body.length}</strong>
          {!opponent.online && <span class="muted"> ({t.snake.offline})</span>}
        </div>
      </div>
      <div class="snake-stage" ref={area}>
        <SnakeBoard
          theme={theme}
          apples={state.apples}
          snakes={state.players.map((p, i) => ({ body: p.body, dir: p.dir, alive: p.alive, mine: i === state.mySeat }))}
          dim={Boolean(overlay)}
        />
        {overlay && <div class="snake-overlay">{overlay}</div>}
      </div>
      <SnakePad onTurn={turn} disabled={!playing} />
    </div>
  );
}
